import * as React from 'react';
import { useStore } from 'react-redux';
import { TextArea } from '../../../components/form-elements/TextArea';
import { useServiceCenter } from '../../../hooks/use-service-center';
import { AutoCompleteItem } from '../../../models/utilities/autocomplete-item';
import { serviceCenterSelectList } from '../../../selectors/userdata-selectors';
import { StatusCode } from '../../../utils/api';
import { AppUtils } from '../../../utils/app-utils';
import { AssetType } from '../../models/enums/asset-type';
import { AssetReference, DriverReference, TruckRead } from '../../models/trucks/truck-read';
import { TransportationSvc } from '../../service';
import { EditDeliveryAssets } from '../../../pages/shipping/trucks/components/EditDeliveryAssets';
import { EditDeliveryBasicInfo } from './EditDeliveryBasicInfo';

interface IProps {
    truck           : TruckRead;
    onTruckChange   : any;
    showCompactUI   : boolean;
}

export const TruckEditBasicInfo = (props: IProps) => {

    const store                         = useStore();
    const userServiceCenter             = useServiceCenter();    
    const [service]                     = React.useState(new TransportationSvc());
    const [editTruck, setEditTruck]     = React.useState<TruckRead>(new TruckRead());
    
    const [serviceCenters] = React.useState(() => {
        return serviceCenterSelectList(store.getState());
    });

    React.useEffect(() => {
        if (props.truck) {    
            setEditTruck({...props.truck});
        }
    }, [props.truck]);

    /**
     * Service Center change handler
     */
    const onServiceCenterChange = (e: any) => {
        const t = {...editTruck} as TruckRead;
        t.serviceCenterId =  e.target.value;

        setEditTruck(t);
        props.onTruckChange(t);
    };

    /**
     * Name change handler
     */
    const onNameChange = (e: any) => {
        const t = {...editTruck} as TruckRead;
        t.name =  e.target.value;
        
        setEditTruck(t);
        props.onTruckChange(t);
    };

    /**
     * Determine whether to disable the service center
     * dropdown
     */
    const shouldDisableServiceCenter = () => {  
        if (editTruck.id !== 0) {
            return true;
        }
        else if (userServiceCenter.name === 'ALL') {
            return false;
        }
        else {
            return true;
        }
    };

    async function getDriver(t : TruckRead) {
        
        if (t && t.driver && t.driver.id) {
            const tmpDriver = await service.getBasicDriver(t.driver.id);
            if (tmpDriver.status === StatusCode.Success && tmpDriver.data.defaultTruckId) {
                const asset = await service.getAsset(tmpDriver.data.defaultTruckId!);
                if (asset.status === StatusCode.Success) {
                    t.tractor = asset.data;                
                    if (asset.data.type === AssetType.BoxTruck) {
                        t.trailer = undefined;
                    }
                }

            }

            setEditTruck(t);
            props.onTruckChange(t);
        }
    }

    const onDriverChange = async (d : AutoCompleteItem) => {

        const t = {...editTruck} as TruckRead;

        if (AppUtils.IsEmpty(d)) {
            t.driver = undefined;
            setEditTruck(t);
            props.onTruckChange(t);
            return;
        }
        
        const tmpDriver     = new DriverReference();        
        tmpDriver.id        = Number.parseInt(d.id, 10);
        tmpDriver.fullName  = d.name;
        t.driver = tmpDriver;

        await getDriver(t);        
    };

    const onTractorChange = (a : AutoCompleteItem) => {
        
        const t = {...editTruck} as TruckRead;

        if (AppUtils.IsEmpty(a)) {
            t.tractor = undefined;
            // t.trailer = undefined;
        }
        else {
            const tmpTractor      = new AssetReference();
            tmpTractor.id         = Number.parseInt(a.id, 10);
            tmpTractor.name       = a.name;
            tmpTractor.type       = a.extraData.type;
            
            if (tmpTractor.type === AssetType.BoxTruck) {
                t.trailer = undefined;
            }

            t.tractor = tmpTractor;
        }

        setEditTruck(t);
        props.onTruckChange(t);
    };

    const onTrailerChange = (a : any) => {

        const t = {...editTruck} as TruckRead;

        if (AppUtils.IsEmpty(a)) {
            t.trailer = undefined;
        }
        else {
            const tmpTrailer      = new AssetReference();
            tmpTrailer.id         = Number.parseInt(a.id, 10);
            tmpTrailer.name       = a.name;
            tmpTrailer.type       = a.extraData.type;   
            t.trailer = tmpTrailer;
        }

        setEditTruck(t);
        props.onTruckChange(t);
    };

    const onCutoffDateChange = (newDate : Date) => {
        const t = {...editTruck} as TruckRead;
        t.cutoffDate = newDate;
        setEditTruck(t);
        props.onTruckChange(t);
    };

    const onNoteChange = (e : any) => {
        const t = {...editTruck} as TruckRead;
        t.note = e.target.value;
        setEditTruck(t);
        props.onTruckChange(t);
    };

    return editTruck
            ? <React.Fragment>

                    <EditDeliveryBasicInfo
                        serviceCenter={ editTruck.serviceCenterId }
                        serviceCenters={ serviceCenters }
                        serviceCenterChange={ onServiceCenterChange }
                        name={ editTruck.name }
                        nameChange={ onNameChange }
                        cutoffDate={ editTruck.cutoffDate }
                        cutoffDateChange={ onCutoffDateChange }
                        showCompactUI={ props.showCompactUI }
                        disable={ false }
                        serviceCenterDisable={ shouldDisableServiceCenter() }
                    />

                    <EditDeliveryAssets
                        showCompactUI={ props.showCompactUI }
                        driver={ editTruck.driver }
                        driverChange={ onDriverChange }
                        tractor={ editTruck.tractor }
                        tractorChange={ onTractorChange }
                        trailer={ editTruck.trailer }
                        trailerChange={ onTrailerChange }
                        deliveryServiceCenter={ editTruck.serviceCenterId }
                        disable={ false }
                        disableTrailer= { editTruck.tractor && (editTruck.tractor.type === AssetType.BoxTruck) ? true  : false }
                    />  

                    <TextArea 
                        id="truck-note-textarea"
                        value={ editTruck.note }
                        label="Note"
                        defaultLines={3}
                        onChange={ onNoteChange }
                    />

            </React.Fragment>
            : null;
};
